/**
 * Derives where a level's lights go, and how big a shadow the sun has to cover.
 *
 * Two problems this fixes, both of which were making the house look like flat cardboard.
 *
 * The first is that one distant directional light lights every surface in a room at the
 * same angle, so nothing has local variation — and with a three-step toon ramp,
 * "roughly the same angle" means "exactly the same colour". A light hanging inside each
 * room is what makes a skirting board's top face brighter than the wall above it and a
 * far corner dimmer than a near one. Without it, relief geometry is invisible.
 *
 * The second is shadow resolution. A fixed shadow frustum spends most of its texels on
 * empty ground outside the house; fitting it to the actual footprint is free resolution,
 * costing neither memory nor fill rate.
 *
 * Pure and headless like the rest of `src/game/`: this returns numbers, and `src/engine`
 * turns them into three.js lights.
 */

import { levelFootprint, type BuiltLevel } from './buildGeometry';
import { boxMin, type GroundPoint, type LevelSpec, type Vec3 } from './types';

/** Warm tungsten, not white — a lit room should feel warmer than the daylight outside. */
export const ROOM_LIGHT_COLOR = '#ffd9a8';

/** How far below the ceiling a room's light hangs. */
const LIGHT_DROP = 0.55;

/**
 * Reach beyond the room's own far corner, so the corner is lit rather than sitting at
 * the very end of the falloff where the "no unlit dead zones" requirement would break.
 */
const LIGHT_REACH = 1.6;

/**
 * Brightness of a room light. Still high because it has physical falloff and hangs near a
 * 4.7 m ceiling: most of its range is spent getting down to head height before it starts
 * crossing the room. Turned down from 14 — lit rooms were flat and washed out, and a
 * lamp reads as a lamp only if the corners it does not reach stay darker than it.
 */
const LIGHT_INTENSITY = 8.4;

/** Slack around the house so a prop by an outside wall still casts into the map. */
export const SHADOW_MARGIN = 1.5;

export type RoomLight = {
  roomId: string;
  position: Vec3;
  color: string;
  intensity: number;
  /** Range at which the light has fallen off to nothing. */
  distance: number;
};

export type ShadowFit = {
  /** Middle of the house on the ground plane. */
  center: GroundPoint;
  /** Half-width of a square frustum that covers the house, plus margin. */
  radius: number;
};

/** Daylight spilling in through one window, placed just inside its opening. */
export type WindowLight = {
  windowId: string;
  position: Vec3;
  color: string;
  intensity: number;
  distance: number;
};

export type Lighting = {
  lights: RoomLight[];
  windowLights: WindowLight[];
  shadow: ShadowFit;
};

/**
 * Storm light: cold and weak, against the warm tungsten indoors. The contrast is what
 * sells both — a window that is merely dimmer than the lamps reads as evening; one that
 * is a different *colour* reads as weather.
 */
export const WINDOW_LIGHT_COLOR = '#9fb6d8';
/**
 * Turned down hard from 1.6, then down again from 0.48 once there was a real night outside
 * to be lit by. Daylight in a storm should be a cold wash on the reveal and the floor under
 * the sill, not a second lamp — at the old values a window lit its half of the room and the
 * tungsten indoors had nothing to contrast with.
 *
 * The floor under this is the visual-style requirement that a player beside a window with
 * the room's lamp off can still make out the room's shape. That is what stops it going to
 * zero; it is not a free dial.
 */
const WINDOW_LIGHT_INTENSITY = 0.3;
/** How far into the room the light sits, so it lights the reveal and sill as well. */
const WINDOW_LIGHT_INSET = 0.35;
const WINDOW_LIGHT_REACH = 4.5;

/**
 * Where the sun stands, relative to the middle of the level.
 *
 * Off one corner and well above, so the shadow frustum below is centred on something it
 * actually contains and nothing casts a shadow straight down its own front. The lift is
 * additive so that a tiny level still gets a sun above its roofline rather than one
 * shining in through the windows.
 */
const SUN_ACROSS = 1;
const SUN_UP = 1.6;
const SUN_ALONG = 0.7;
const SUN_LIFT = 6;

/**
 * Where the *moon* stands over a grounds level: the same corner, far steeper — ~75° up
 * instead of the house's ~55°.
 *
 * The house's angle is authored for architecture seen from outside its shadows. Inside a
 * maze it is wrong twice over: a 3 m hedge throws a 2.1 m hard toon shadow diagonally
 * across a 2.4 m corridor, so sunlit strips land at the feet of hedges whose faces are
 * dark — reading as light leaking out from *under* the hedge — and every hedge stub drops
 * a long sideways block of shadow that looks unmoored from its caster. Overhead light
 * keeps each hedge's shadow at its own feet (~0.65 m wide) and the corridors legible.
 * The azimuth keeps the corner direction so shadows still point somewhere findable, and
 * the moon — hung along this same line by `sunDirection` — rides high and stays visible.
 */
const MOON_ACROSS = 0.5;
const MOON_UP = 2.4;
const MOON_ALONG = 0.35;

/** Which light a level gets is a fact about the level, so it is read off `built`. */
function sunOffset(built: BuiltLevel, shadow: ShadowFit): Vec3 {
  const overhead = built.grounds !== null;
  return {
    x: shadow.radius * (overhead ? MOON_ACROSS : SUN_ACROSS),
    y: shadow.radius * (overhead ? MOON_UP : SUN_UP) + SUN_LIFT,
    z: shadow.radius * (overhead ? MOON_ALONG : SUN_ALONG),
  };
}

/** Where to put the light itself. */
export function sunPosition(built: BuiltLevel): Vec3 {
  const shadow = fitShadow(built);
  const offset = sunOffset(built, shadow);
  return { x: shadow.center.x + offset.x, y: offset.y, z: shadow.center.z + offset.z };
}

/**
 * The same thing as a unit vector: which way it is to the sun from the middle of the level.
 *
 * This exists so the moon can be hung out along it (design D9). The two have to agree
 * exactly — a moon that is not where the shadows point from is worse than no moon at all,
 * because the player can see both at once — and the only way to guarantee that is for both
 * to come out of one function rather than two places multiplying the same three numbers.
 * Both take the built level rather than a `ShadowFit` for the same reason: the offset now
 * depends on whether the level is grounds, and a flag the callers pass separately is a
 * flag two callers can disagree on.
 */
export function sunDirection(built: BuiltLevel): Vec3 {
  const offset = sunOffset(built, fitShadow(built));
  // Never zero: `SUN_LIFT` is metres rather than a multiple of the radius, so even a level
  // with no floors at all has a sun 6 m above the origin.
  const length = Math.hypot(offset.x, offset.y, offset.z);
  return { x: offset.x / length, y: offset.y / length, z: offset.z / length };
}

export function buildLighting(built: BuiltLevel, level: LevelSpec): Lighting {
  const ceilingHeight = built.ceilings.length > 0 ? boxMin(built.ceilings[0]!.box).y : 0;
  const authored = new Map(level.rooms.map((room) => [room.id, room.light]));

  const lights = built.floors.map((floor): RoomLight => {
    const spec = authored.get(floor.roomId);
    const halfDiagonal = Math.hypot(floor.box.size.x, floor.box.size.z) / 2;
    const drop = spec?.drop ?? LIGHT_DROP;
    // Never below halfway, or an "authored" light ends up on the floor.
    const height = Math.max(ceilingHeight - drop, ceilingHeight / 2);

    return {
      roomId: floor.roomId,
      position: {
        x: floor.box.center.x + (spec?.offset?.x ?? 0),
        y: height,
        z: floor.box.center.z + (spec?.offset?.z ?? 0),
      },
      color: spec?.color ?? ROOM_LIGHT_COLOR,
      intensity: spec?.intensity ?? LIGHT_INTENSITY,
      // The far *bottom* corner, not the far corner of the floor plan: a fitting near a
      // 4.7 m ceiling spends most of its reach coming down before it crosses the room,
      // and a range that ignores the height leaves every wall dark.
      distance: Math.hypot(halfDiagonal, height) + LIGHT_REACH,
    };
  });

  const windowLights = built.windowFaces.map(
    (face): WindowLight => ({
      windowId: face.windowId,
      position: {
        x: face.center.x + face.inward.x * WINDOW_LIGHT_INSET,
        y: face.center.y,
        z: face.center.z + face.inward.z * WINDOW_LIGHT_INSET,
      },
      color: WINDOW_LIGHT_COLOR,
      intensity: WINDOW_LIGHT_INTENSITY,
      distance: WINDOW_LIGHT_REACH,
    }),
  );

  return { lights, windowLights, shadow: fitShadow(built) };
}

/**
 * The sun's shadow frustum: where to centre it, and how wide it has to be.
 *
 * Exported as well as used here because the moon is hung off the same fit — everything
 * about where the light comes from is derived from this one number, so anything that has to
 * agree with the sun asks for it rather than re-deriving it.
 */
export function fitShadow(built: BuiltLevel): ShadowFit {
  const { center, halfSpan, radius } = levelFootprint(built);

  // Indoors, `halfSpan` — half the longer side — is enough: the sun's frustum is an
  // axis-aligned box, and the corners of a house's plan are behind its walls anyway.
  //
  // Outdoors it is not. The sun is placed off one corner of the footprint (LevelLighting),
  // so the shadows it throws run along the diagonal, and everything the box misses renders
  // fully lit. On grounds — where nothing is behind a wall, the lawn is 48 m across and
  // the miss is an ~8 m strip at two corners with hedge shadows stopping dead along a
  // diagonal line — the frustum has to reach the corner itself, which is `radius`.
  const fitted = built.grounds ? radius : halfSpan;
  return { center, radius: fitted + SHADOW_MARGIN };
}
